import React from 'react';
import { useTranslation } from 'react-i18next';

import { tracking } from 'App/Tracking';
import { multiDestinationLogger } from 'App/Tracking/multiDestinationLogger';
import { LocationForm } from 'Shared/LocationForm';
import leftPhoto from './assets/practitioner-02.jpg';
import centrePhoto from './assets/practitioner-07.jpg';
import rightPhoto from './assets/practitioner-09.jpg';

/**
 * Asks a Talk user without a country of residence to pick one, inline in place
 * of the practitioners row.
 *
 * `/book` asks the same question through `LocationModal`, which blocks the whole
 * page; here every other section must stay usable, so it is asked in place.
 *
 * Saving writes into the `USER_LOCATION_QUERY` cache entry, which flips
 * `hasValidLocationData` for every consumer — including `PractitionersRow`,
 * which then re-renders with practitioners. No navigation, no reload.
 */
const LocationEmptyState = () => {
  const { t } = useTranslation('homepage', {
    keyPrefix: 'therapy_and_coaching_section.practitioners',
  });

  return (
    <>
      <h2 className="title-s section-title">{t('title')}</h2>
      <div className="card secondary no-border therapy-and-coaching__empty">
        {/* Decorative — these are not the practitioners the user will be
            shown. Each is wrapped in a DS card purely for its surface-coloured
            frame, since no colour token is reachable from Sass here. */}
        <div className="therapy-and-coaching__fan" aria-hidden="true">
          {[
            { photo: leftPhoto, position: 'left' },
            { photo: rightPhoto, position: 'right' },
            { photo: centrePhoto, position: 'centre' },
          ].map(({ photo, position }) => (
            <span
              key={position}
              className={`card primary no-border therapy-and-coaching__fan-photo therapy-and-coaching__fan-photo--${position}`}
            >
              <img src={photo} alt="" />
            </span>
          ))}
        </div>
        <h3 className="title-m therapy-and-coaching__empty-heading">
          {t('empty.heading')}
        </h3>
        <p className="body-s therapy-and-coaching__empty-body">
          {t('empty.body')}
        </p>
        <div className="therapy-and-coaching__empty-controls">
          <LocationForm
            onSubmitSuccess={({ country, state }) => {
              tracking.track('place-of-residence-updated', {
                country,
                state,
                source: 'home',
              });

              void multiDestinationLogger({
                eventName: 'talk-location-set',
              });
            }}
          />
        </div>
      </div>
    </>
  );
};

export default LocationEmptyState;
